import React, { useState, useMemo } from "react";
import apiClient from "../../../core/config/api";
import "../manage_users.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onUserCreated: () => void;
};

const AddUserModal: React.FC<Props> = ({ isOpen, onClose, onUserCreated }) => {
  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("active");
  const [credits, setCredits] = useState("100");
  const [autoRefill, setAutoRefill] = useState(true);
  const [sendInvite] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rootUserCredits, setRootUserCredits] = useState({ total_credits: 0, available_credits: 0 });

  const sanitizeEmailInput = (raw: string) => raw.replace(/[^a-zA-Z0-9@._+\-]/g, "");
  const isEmailPatternValid = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const parseCredits = (raw: string) => {
    const cleaned = raw.replace(/[^0-9]/g, "");
    return cleaned === "" ? 0 : parseInt(cleaned, 10);
  };

  const formatCreditsInput = (raw: string) => {
    const num = parseCredits(raw);
    return num === 0 ? "" : num.toLocaleString("en-US");
  };
  const formatNumber = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return "0";
    return value.toLocaleString("en-US");
  };

  const cleanedEmail = useMemo(() => email.trim(), [email]);
  const isEmailValid = useMemo(() => {
    if (cleanedEmail === "") return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanedEmail);
  }, [cleanedEmail]);

  const displayName = useMemo(() => {
    const parts = fullName.trim().split(" ").filter(Boolean);
    return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(" ");
  }, [fullName]);

  const isFormValid = Boolean(fullName.trim() !== "" && isEmailValid && status);
  const isCreditsValid = parseCredits(credits) > 0;
  const canProceed = !autoRefill || isCreditsValid;
  const availablePool = Number(rootUserCredits.available_credits || 0);
  const creditsToAssign = parseCredits(credits) || 0;
  const overLimit = creditsToAssign > availablePool;

  React.useEffect(() => {
    if (isOpen) {
      apiClient.get('/account/re/credits/', { params: { _t: Date.now() } })
        .then((res: any) => {
          setRootUserCredits({
            total_credits: res.data.max_upload_limit || 0,
            available_credits: res.data.remaining_credits || 0,
          });
        })
        .catch(() => {});
    }
  }, [isOpen]);

  const handleCreateSubUser = async () => {
    try {
      setLoading(true);
      setError("");

      if (overLimit) {
        setError(`Low balance: available ${formatNumber(availablePool)}`);
        setStep(2);
        return;
      }

      const cleanedName = fullName.trim();
      if (!isEmailValid) {
        setError("Please enter a valid email address");
        setLoading(false);
        return;
      }
      const [firstName, ...lastNameParts] = cleanedName.split(' ');
      const lastName = lastNameParts.join(' ');

      const tempPass = `Temp${Math.random().toString(36).slice(-8)}!`;
      const userData: any = {
        first_name: firstName,
        last_name: lastName || "",
        email: cleanedEmail,
        phone_number: "",
        job_title: "",
        password: tempPass,
        confirm_password: tempPass,
        role_id: 6,
        account_status: status,
        is_verified: status === "active",
        auto_refill: autoRefill,
        send_invite: sendInvite,
        credits: parseCredits(credits),
      };

      userData.confirm_password = userData.password;

      await apiClient.post('/re/sub-users', userData);

      setStep(4);
      onUserCreated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.response?.data?.message || 'Failed to create user');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFullName('');
    setEmail('');
    setStatus('active');
    setCredits('100');
    setAutoRefill(true);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay" onClick={handleClose}>
      <div
        className="popup-box"
        style={{
          backgroundImage: "url('/images/popup-back.png')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "inherit",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {step === 4 ? (
          <div className="d-flex flex-row justify-content-end">
            <img
              src="/images/cancel.png"
              alt="close"
              style={{ cursor: "pointer" }}
              onClick={onClose}
            />
          </div>
        ) : (
          <div className="d-flex flex-row justify-content-between">
            <p className="user-info">{step === 1 ? "User Information" : step === 2 ? "Credits & Invitation" : "Review & Confirm"}</p>
            <img
              src="/images/cancel.png"
              alt="close"
              style={{ cursor: "pointer" }}
              onClick={onClose}
            />
          </div>
        )}

        {step === 1 && (
          <>
            {error && (
              <div className="email-alert-danger mt-2" style={{ marginBottom: 12 }}>
                {error}
              </div>
            )}
            <form className="user-form">
              <div className="m-form-group">
                <label className="m-label">Full Name</label>
                <input
                  type="text"
                  className="m-form-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>

              <div className="m-form-group">
                <label className="m-label">Email</label>
                <input
                  type="email"
                  className="m-form-input"
                  value={email}
                  onChange={(e) => {
                    setError("");
                    setEmail(sanitizeEmailInput(e.target.value));
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    const cleaned = sanitizeEmailInput(pasted);
                    if (!isEmailPatternValid(cleaned)) {
                      e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                    setError("");
                    setEmail(cleaned);
                  }}
                />
              </div>

              <div className="m-form-group">
                <label className="m-label">Status</label>
                <select
                  className="m-form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </form>

            <div className="d-flex flex-row justify-content-end">
              <div className="popup-actions">
                <button
                  type="button"
                  className="save"
                  disabled={!isFormValid || loading}
                  onClick={() => {
                    setError("");
                    setStep(2);
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="m-form-group">
              <label className="m-label">Credits to Assign</label>
              <input
                type="text"
                className="m-form-input"
                value={formatCreditsInput(credits)}
                onChange={(e) => {
                  const nextValue = formatCreditsInput(e.target.value);
                  setCredits(nextValue);
                  setAutoRefill(parseCredits(nextValue) > 0);
                }}
                inputMode="numeric"
              />
            </div>

            {overLimit && (
              <div className="email-alert-danger mt-2" style={{ marginBottom: 12 }}>
                {`Low balance: available ${formatNumber(availablePool)}`}
              </div>
            )}

            <div className="remaining-box">
              <div className="d-flex flex-row justify-content-between align-items-center remaining-row">
                <div className="remaining-label">
                  <p className="remaining">Remaining in Pool</p>
                  <span className="remaining-colon">:</span>
                </div>
                <p className="remainin-pool">{formatNumber(rootUserCredits.available_credits)}</p>
              </div>
            </div>

            <div className="toggle-battle">
              <div className="d-flex flex-row justify-content-between align-items-center">
                <p className="auto-refill">Auto-refill</p>
                <p className="auto-refill">:</p>

                <label className="toggle-wrapper">
                  <input
                    type="checkbox"
                    checked={autoRefill}
                    onChange={(e) => setAutoRefill(e.target.checked)}
                  />
                  <div className="toggle-track">
                    <span className="toggle-text on">ON</span>
                    <span className="toggle-text off">OFF</span>
                    <span className="toggle-knob"></span>
                  </div>
                </label>
              </div>
            </div>

            <div className="d-flex flex-row justify-content-between">
              <div>
                <button type="button" className="cancel" onClick={() => setStep(1)}>
                  Back
                </button>
              </div>
              <div>
                <button
                  type="button"
                  className="save"
                  disabled={!canProceed || overLimit}
                  onClick={() => {
                    if (overLimit) {
                      setError(`Low balance: available ${formatNumber(availablePool)}`);
                      return;
                    }
                    setError("");
                    setStep(3);
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            {error && <div className="email-alert-danger mt-2">{error}</div>}

            <div className="d-flex flex-row justify-content-center mt-3">
              <div className="bottom-input-group">
                <label className="bottom-label">Name</label>
                <input type="text" className="f-input" value={displayName} readOnly />
              </div>

              <div className="bottom-input-group">
                <label className="bottom-label">Status</label>
                <input
                  type="text"
                  className="f-input"
                  value={status === "active" ? "Active" : status === "inactive" ? "Inactive" : status}
                  readOnly
                />
              </div>
            </div>

            <div className="d-flex flex-row justify-content-center mt-3">
              <div className="bottom-input-group">
                <label className="bottom-label">Email</label>
                <input type="text" className="f-input" value={email} readOnly />
              </div>

              <div className="bottom-input-group">
                <label className="bottom-label">Credits Assigned</label>
                <input
                  type="text"
                  className="f-input"
                  value={credits ? formatNumber(parseCredits(credits)) : "N/A"}
                  readOnly
                />
              </div>
            </div>

            <div className="d-flex flex-row justify-content-center mt-3">
              <div className="bottom-input-group">
                <label className="bottom-label">Role</label>
                <input type="text" className="f-input" value="Member" readOnly />
              </div>

              <div className="bottom-input-group">
                <label className="bottom-label">Auto-Refill</label>
                <input type="text" className="f-input" value={autoRefill ? "Enabled" : "Disabled"} readOnly />
              </div>
            </div>

            <div className="d-flex flex-row justify-content-between mt-4">
              <div>
                <button
                  type="button"
                  className="f-cancel"
                  style={{ backgroundColor: '#fff', color: '#115EA3', border: '1px solid #115EA3', padding: '4px 30px' }}
                  onClick={() => setStep(2)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>

              <div className="d-flex flex-row justify-content-center">
                <button
                  type="button"
                  className="f-confirm"
                  onClick={handleCreateSubUser}
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Confirm"}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div>
              <img src="/images/shadow-check.png" className="invitation-img" alt="" />
              <p className="email-invitation">Email Sent Successfully</p>

              <p className="invitation" style={{ textAlign: 'center' }}>
                We&apos;ve sent an invitation to the user. They&apos;ll receive an email shortly with a link to
                activate their account and get started.
              </p>

              <div className="d-flex flex-row justify-content-center" style={{ marginTop: 30 }}>
                <button type="button" className="f-confirm" onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddUserModal;
